import api from '../../utils/api'
import { useState, useEffect } from 'react'

import Input from '../../shared/components/form/input'
import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import useFlashMessage from '../../hooks/useFlashMessage'

import './Production.css'

function itemLabel(item) {
    if (!item) return 'Item'
    const parts = []
    if (item.category) parts.push(item.category.label || item.category.name)
    if (item.cloth) parts.push(item.cloth.label || item.cloth.name)
    if (item.sex) parts.push(item.sex.label)
    const text = parts.filter(Boolean).join(' · ')
    return item.simpleProduct ? item.description : (text || item.description || 'Item')
}

function statusLabel(status) {
    if (status === 'em_transito') return 'Em trânsito'
    if (status === 'recebido') return 'Recebido'
    return 'Finalizado'
}

function renderPackageTree(packages, parentId, depth) {
    return packages
        .filter((p) => String(p.parentPackage || null) === String(parentId || null))
        .map((p) => (
            <div key={p._id} className="opd-package" style={{ marginLeft: depth * 18 }}>
                <div className="opd-package-main">
                    <span className="opd-package-sector">{p.fromSector || 'Estoque'} → {p.toSector}</span>
                    <span className="opd-package-qty">{p.sentQuantity} {p.unit?.label || p.unit?.value}</span>
                    <span className={`opd-package-status opd-package-status-${p.status}`}>{statusLabel(p.status)}</span>
                </div>
                <span className="opd-package-history-item">
                    Saída: {new Date(p.sentAt).toLocaleString('pt-BR')}{p.sentBy?.name ? ` — ${p.sentBy.name}` : ''}
                </span>
                {p.receivedAt && (
                    <span className="opd-package-history-item">
                        Entrada: {p.receivedQuantity} {p.unit?.label || p.unit?.value} em {new Date(p.receivedAt).toLocaleString('pt-BR')}{p.receivedBy?.name ? ` — ${p.receivedBy.name}` : ''}
                    </span>
                )}
                {p.obs && <span className="opd-package-history-item">Obs: {p.obs}</span>}
                {renderPackageTree(packages, p._id, depth + 1)}
            </div>
        ))
}

function OpDetail({ productionOrderId, myPermissionEdit, btCancelAction, handleOnChange }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [productionOrder, setProductionOrder] = useState(null)
    const [packages, setPackages] = useState([])
    const [expectedConsumption, setExpectedConsumption] = useState(null)
    const [load, setLoad] = useState(false)

    const [consumptionModal, setConsumptionModal] = useState(false)
    const [consumptionRows, setConsumptionRows] = useState([])

    function getData() {
        api.get(`/production/op/${productionOrderId}`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setProductionOrder(response.data.productionOrder)
            setPackages(response.data.packages || [])
            setExpectedConsumption(response.data.expectedConsumption)
            setLoad(true)
        })
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productionOrderId])

    function openConsumption() {
        const aviamentoNameById = {}
        ;(productionOrder.itemSnapshot?.aviamentos || []).forEach((entry) => {
            const avId = entry.aviamento?._id || entry.aviamento
            if (avId) aviamentoNameById[avId] = entry.aviamento?.name
        })

        const rows = []
        if (expectedConsumption?.cloth) {
            rows.push({
                materialType: 'cloth',
                materialId: expectedConsumption.cloth.clothId,
                name: productionOrder.itemSnapshot?.cloth?.name,
                provider: expectedConsumption.cloth.providerId,
                unit: expectedConsumption.cloth.unit,
                expectedQuantity: expectedConsumption.cloth.quantity,
                quantity: expectedConsumption.cloth.quantity.toFixed(2),
            })
        }
        ;(expectedConsumption?.aviamentos || []).forEach((av) => {
            rows.push({
                materialType: 'aviamento',
                materialId: av.aviamentoId,
                name: aviamentoNameById[av.aviamentoId] || 'Aviamento',
                provider: undefined,
                unit: av.unit,
                expectedQuantity: av.quantity,
                quantity: av.quantity.toFixed(2),
            })
        })

        setConsumptionRows(rows)
        setConsumptionModal(true)
    }

    function updateConsumptionRow(index, value) {
        setConsumptionRows((prev) => prev.map((r, i) => i === index ? { ...r, quantity: value } : r))
    }

    async function submitConsumption() {
        const items = consumptionRows.map((r) => ({
            materialType: r.materialType,
            cloth: r.materialType === 'cloth' ? r.materialId : undefined,
            aviamento: r.materialType === 'aviamento' ? r.materialId : undefined,
            provider: r.provider,
            unit: r.unit,
            quantity: parseFloat(r.quantity) || 0,
        }))

        let msgType = 'success'
        const data = await api.patch(`/production/op/${productionOrderId}/closeConsumption`, { items }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setConsumptionModal(false)
            getData()
            handleOnChange && handleOnChange()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando Ordem de Produção...</div>
    }

    const hasMaterial = !!(expectedConsumption?.cloth || (expectedConsumption?.aviamentos || []).length > 0)
    const totalCosts = (productionOrder.costs || []).reduce((acc, c) => acc + c.value, 0)

    return (
        <div className="opd">
            <div className="opd-header">
                <p className="opd-title">{productionOrder.code} — Pedido #{productionOrder.order?.code}</p>
                <span className="opd-title-accent" />
                <p className="opd-subtitle">{productionOrder.order?.client?.name} · {itemLabel(productionOrder.itemSnapshot)} · {productionOrder.quantity} un.</p>
            </div>

            <div className="opd-section">
                <p className="opd-section-title">Material</p>
                {!productionOrder.materialConsumed ? (
                    hasMaterial ? (
                        <>
                            {expectedConsumption.cloth && (
                                <div className="opd-material-row">
                                    <span>{productionOrder.itemSnapshot?.cloth?.name}</span>
                                    <span>{expectedConsumption.cloth.quantity.toFixed(2)} {expectedConsumption.cloth.unit?.label || expectedConsumption.cloth.unit?.value} (esperado)</span>
                                </div>
                            )}
                            {(expectedConsumption.aviamentos || []).map((av, i) => (
                                <div key={i} className="opd-material-row">
                                    <span>Aviamento</span>
                                    <span>{av.quantity.toFixed(2)} {av.unit?.label || av.unit?.value} (esperado)</span>
                                </div>
                            ))}
                            {myPermissionEdit && (
                                <Button variant="save" onClick={openConsumption}>Fechar Consumo de Material</Button>
                            )}
                        </>
                    ) : (
                        <p className="opd-empty">Esse item não tem material associado.</p>
                    )
                ) : (
                    (productionOrder.consumedLots || []).map((r, i) => (
                        <div key={i} className="opd-material-row">
                            <span>{r.materialType === 'aviamento' ? r.aviamento?.name : r.cloth?.name}</span>
                            <span>{r.quantity.toFixed ? r.quantity.toFixed(2) : r.quantity} {r.unit?.label || r.unit?.value}</span>
                        </div>
                    ))
                )}
            </div>

            <div className="opd-section">
                <p className="opd-section-title">Custos lançados{totalCosts > 0 ? ` — total: R$ ${totalCosts.toFixed(2)}` : ''}</p>
                {(productionOrder.costs || []).length > 0 ? (
                    productionOrder.costs.map((c, i) => (
                        <div key={i} className="opd-material-row">
                            <span>{c.description}{c.sector ? ` — ${c.sector}` : ''}</span>
                            <span>R$ {c.value.toFixed(2)}</span>
                        </div>
                    ))
                ) : (
                    <p className="opd-empty">Nenhum custo lançado ainda.</p>
                )}
            </div>

            <div className="opd-section">
                <p className="opd-section-title">Pacotes</p>
                {renderPackageTree(packages, null, 0)}
            </div>

            <div className="opd-footer">
                <Button variant="cancel" onClick={btCancelAction}>Fechar</Button>
            </div>

            <Modal
                modalStatus={consumptionModal}
                changeStatus={() => setConsumptionModal(false)}
                w={45}
                content={
                    <div className="opd-consumption">
                        <p className="opd-consumption-title">Fechar Consumo de Material</p>
                        <span className="opd-consumption-title-accent" />
                        {consumptionRows.map((r, i) => (
                            <Input
                                key={i}
                                text={`${r.name} (esperado: ${r.expectedQuantity.toFixed(2)} ${r.unit?.label || r.unit?.value})`}
                                type="number"
                                name={`consumption-${i}`}
                                placeholder="0.00"
                                handleOnChange={(e) => updateConsumptionRow(i, e.target.value)}
                                value={r.quantity}
                            />
                        ))}
                        <div className="opd-consumption-actions">
                            <Button variant="cancel" onClick={() => setConsumptionModal(false)}>Cancelar</Button>
                            <Button variant="save" onClick={submitConsumption}>Confirmar</Button>
                        </div>
                    </div>
                }
            />
        </div>
    )
}

export default OpDetail
